import React, { Component } from 'react';
import PropTypes from 'prop-types';

import styled, { css } from 'styled-components';

import { Box, Flex } from '../Box/Box';
import Icon from '../Icon/Icon';
import Spinner from '../Spinner/Spinner';

import DefaultDropdownItems from './DefaultDropdownItems/DefaultDropdownItems';

import DomUtils from '../../../utils/domUtils';

const DropdownArrow = styled(Icon).attrs({
  icon: 'dropdownArrow'
})`
  position: absolute;
  height: 5px;
  width: 10px;
  top: 2px;
  bottom: 0;
  right: 12px;
  margin: auto 0;
`;

const Wrapper = styled(Box)`
  position: relative;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};

  & ${DropdownArrow} {
    display: ${props =>
      props.disabled || props.type === 'borderless' ? 'none' : 'block'};
    transform: ${props => {
      if (props.direction === 'up') {
        return props.active ? 'none' : 'rotate(180deg)';
      }
      return props.active ? 'rotate(180deg)' : 'none';
    }};
  }

  &::after {
    content: '';
    position: absolute;
    display: ${props =>
      props.active && props.type !== 'borderless' ? 'block' : 'none'};
    background-color: #cdcdcd;
    width: calc(100% - 2px);
    height: 1px;
    z-index: 2050;
    left: 1px;
    ${props => (props.direction === 'up' ? 'top: 0' : 'bottom: 0')};
  }
`;

const ValueContainer = styled(Box)`
  padding: ${props => props.padding || '0 0 0 10px'};
  font-size: ${props => props.fontSize || 'inherit'};
  font-weight: ${props => props.fontWeight || 'normal'};
  line-height: ${props => props.lineHeight || '1.33'};
  letter-spacing: ${props => props.spacing || 'normal'};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const ErrorContainer = styled(Box)`
  position: absolute;
  color: #ee1c1c;
  margin-top: 5px;
  font-size: ${props => props.fontSize || '13px'};
  font-family: ${props => props.theme.fonts.regular};
  line-height: ${props => props.lineHeight || '1'};
  left: 0;
`;

const ItemsContainer = styled(Box)`
  overflow-y: auto;
`;

const ItemsWrapper = styled(Box)`
  position: absolute;
  ${props => (props.align === 'right' ? 'right: 0' : 'left: 0')};
  width: ${props => props.width || '100%'};
  min-width: ${props => props.minWidth || '100%'};
  background-color: ${props => props.theme.colors.white};
  font-family: ${props => props.theme.fonts.regular};
  overflow: hidden;
  z-index: 2040;

  & ${ItemsContainer} {
    max-height: ${props => props.maxHeight || '200px'};
  }
`;

const borderlessBaseStyles = css`
  width: 100%;

  ${props =>
    props.active
      ? css`
          box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.19),
            0 3px 6px 0 rgba(0, 0, 0, 0.12);
          ${props.direction === 'up'
            ? css`
                border-top-left-radius: 0;
                border-top-right-radius: 0;
                border-top: 1px solid transparent;
              `
            : css`
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
                border-bottom: 1px solid transparent;
              `};
        `
      : ''};

  & + ${ItemsWrapper} {
    box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.19), 0 3px 6px 0 rgba(0, 0, 0, 0.12);
  }
`;

const borderedBaseStyles = css`
  width: 100%;
  border: 1px solid;

  ${props => {
    if (props.active) {
      return css`
        border-color: ${props.theme.colors.brand};
        box-shadow: 0 2px 4px 0 rgba(36, 75, 137, 0.22);
        ${props.direction === 'up'
          ? css`
              border-top-left-radius: 0;
              border-top-right-radius: 0;
              border-top: 1px solid transparent;
            `
          : css`
              border-bottom-left-radius: 0;
              border-bottom-right-radius: 0;
              border-bottom: 1px solid transparent;
            `};
      `;
    }

    if (props.error && !props.active && props.touched) {
      return css`
        border-color: #ee1d1d;
        background-color: rgba(238, 29, 29, 0.05);
      `;
    }
    if (props.disabled) {
      return css`
        border-color: rgba(0, 0, 0, 0.03);
        background-color: #f8f8f8;
      `;
    }
    return css`
      border-color: #9b9b9b;
    `;
  }};

  & + ${ItemsWrapper} {
    border: 1px solid ${props => props.theme.colors.brand};
  }
`;

const smallFieldStyles = css`
  height: ${props => props.height || '30px'};
  border-radius: 5px;
  font-size: 15px;
  line-height: 1;

  & + ${ItemsWrapper} {
    ${props =>
      props.direction === 'up'
        ? css`
            bottom: calc(${props.height || '30px'} - 1px);
            border-radius: 5px 5px 0 0;
          `
        : css`
            top: calc(${props.height || '30px'} - 1px);
            border-radius: 0 0 5px 5px;
          `};
  }
`;

const mediumFieldStyles = css`
  height: ${props => props.height || '40px'};
  border-radius: 6px;
  font-size: 18px;
  line-height: 1;

  & + ${ItemsWrapper} {
    ${props =>
      props.direction === 'up'
        ? css`
            bottom: calc(${props.height || '40px'} - 1px);
            border-radius: 6px 6px 0 0;
          `
        : css`
            top: calc(${props.height || '40px'} - 1px);
            border-radius: 0 0 6px 6px;
          `};
  }
`;

const Field = styled(Flex).attrs({
  justify: 'flex-start',
  align: 'center'
})`
  padding: ${props => props.innerPadding};
  font-family: ${props => props.theme.fonts.regular};

  ${props => {
    switch (props.size) {
      case 'small':
        return smallFieldStyles;
      case 'medium':
      default:
        return mediumFieldStyles;
    }
  }} ${props => {
      switch (props.type) {
        case 'borderless':
          return borderlessBaseStyles;
        case 'bordered':
        default:
          return borderedBaseStyles;
      }
    }};
`;

class Dropdown extends Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium']),
    type: PropTypes.oneOf(['bordered', 'borderless']),
    direction: PropTypes.oneOf(['down', 'up']),
    width: PropTypes.string,
    innerPadding: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    valueParams: PropTypes.shape({
      padding: PropTypes.string,
      fontWeight: PropTypes.string,
      lineHeight: PropTypes.string,
      spacing: PropTypes.string
    }),
    valueTemplate: PropTypes.element,
    items: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.object),
      PropTypes.func
    ]),
    itemsParams: PropTypes.shape({
      align: PropTypes.oneOf(['left', 'right']),
      minWidth: PropTypes.string,
      maxHeight: PropTypes.string
    }),
    itemsTemplate: PropTypes.element,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    errorParams: PropTypes.object,
    disabled: PropTypes.bool,
    onChange: PropTypes.func
  };

  static defaultProps = {
    placeholder: '',
    size: 'medium',
    type: 'bordered',
    direction: 'down',
    width: 'auto',
    innerPadding: '0 20px 0 0',
    value: '',
    valueParams: {},
    valueTemplate: null,
    items: null,
    itemsParams: {},
    itemsTemplate: null,
    error: null,
    errorParams: {},
    disabled: false,
    onChange: () => {}
  };

  state = {
    active: false,
    loading: false,
    loaded: false,
    loadedItems: null,
    loadError: false,
    touched: false
  };

  componentWillUpdate(nextProps, nextState) {
    if (!this.state.active && nextState.active) {
      document.addEventListener('click', this.handleDocumentClick, false);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.active && !this.state.active) {
      document.removeEventListener('click', this.handleDocumentClick, false);
    }
  }

  componentWillUnmount() {
    if (this.state.active) {
      document.removeEventListener('click', this.handleDocumentClick, false);
    }
  }

  handleDocumentClick = event => {
    if (this.state.active && !DomUtils.targetIsDescendant(event, this)) {
      this.close();
    }
  };

  getItems = () => {
    const { items } = this.props;
    const { loadedItems } = this.state;

    if (typeof items !== 'function') return items;
    if (!this.state.loaded) return [];

    return loadedItems && loadedItems.length ? loadedItems : [];
  };

  getDisabled = () => {
    const { disabled, items } = this.props;
    const { loaded, loadedItems, loadError } = this.state;

    if (disabled) return true;

    if (typeof items !== 'function') {
      return items.length < 1;
    }

    if (loadError) return true;
    return loaded ? !loadedItems.length || loadedItems.length === 0 : false;
  };

  handleChange = value => {
    const { onChange } = this.props;

    if (onChange) {
      onChange(value);
    }

    this.close();
  };

  open = async () => {
    const { items } = this.props;

    if (this.getDisabled()) return;

    if (this.state.active) {
      this.close();
      return;
    }

    if (typeof items !== 'function') {
      this.setState({
        active: true,
        touched: true
      });
      return;
    }

    if (this.state.loading || this.state.loaded) {
      this.setState({
        active: true,
        touched: true
      });
      return;
    }

    this.setState({
      active: true,
      loading: true,
      touched: true
    });

    try {
      const dynamicItems = await items();
      this.setState({
        loadedItems: dynamicItems,
        loading: false,
        loaded: true
      });
    } catch (e) {
      this.setState({
        active: false,
        loading: false,
        loadError: true
      });
    }
  };

  close = () => {
    this.setState({
      active: false
    });
  };

  renderItems = () => {
    const { value, itemsTemplate } = this.props;

    if (this.state.loading) {
      return (
        <Box height="50px">
          <Spinner size="tiny" />
        </Box>
      );
    }

    const template = itemsTemplate || <DefaultDropdownItems />;

    return React.cloneElement(template, {
      value,
      items: this.getItems(),
      onChange: this.handleChange
    });
  };

  renderValue = () => {
    const { value, placeholder, valueTemplate } = this.props;
    const selectedItem = this.getItems().find(item => item.value === value);

    if (valueTemplate) {
      return React.cloneElement(valueTemplate, {
        value: selectedItem || value || null
      });
    }

    if (!selectedItem)
      return (value && value !== 'null' && value) || placeholder || '';

    return selectedItem.label;
  };

  render() {
    const {
      valueParams,
      itemsParams,
      name,
      value,
      width,
      disabled,
      ...restProps
    } = this.props;

    return (
      <Wrapper
        type={this.props.type}
        onClick={this.open}
        width={width}
        direction={this.props.direction}
        active={this.state.active}
        disabled={this.getDisabled()}
      >
        <Field
          className="dropdown-field"
          active={this.state.active}
          touched={this.state.touched}
          disabled={this.getDisabled()}
          {...restProps}
        >
          <ValueContainer className={`dropdown-${name}`} {...valueParams}>
            {this.renderValue()}
          </ValueContainer>
        </Field>

        {this.state.active && (
          <ItemsWrapper className="dropdown-items" {...itemsParams}>
            <ItemsContainer>{this.renderItems()}</ItemsContainer>
          </ItemsWrapper>
        )}

        {this.props.error &&
          this.state.touched && (
            <ErrorContainer
              className="dropdown-error"
              {...this.props.errorParams}
            >
              {this.props.error}
            </ErrorContainer>
          )}

        <DropdownArrow />
      </Wrapper>
    );
  }
}

export default Dropdown;



// WEBPACK FOOTER //
// ./src/components/units/Dropdown/Dropdown.js